// src/pages/MyApprovalsPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Container, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, IconButton,
  TextField, InputAdornment, Alert, Skeleton, Stack,
  Tabs, Tab,
} from '@mui/material';

import SearchIcon         from '@mui/icons-material/Search';
import ClearIcon          from '@mui/icons-material/Clear';
import HowToRegIcon       from '@mui/icons-material/HowToReg';
import HistoryIcon        from '@mui/icons-material/History';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

import { useAuth }                from '../contexts/AuthContext';
import { useToast }               from '../contexts/ToastContext';
import { assetService }           from '../services/assetService';
import { preferencesService }     from '../services/preferencesService';
import { singleValueListService } from '../services/singleValueListService';
import { logAuditEvent }          from '../utils/logAudit';
import {
  sendNextLevelApprovalEmail,
  sendApprovalStatusEmail,
} from '../services/brevoEmailService';
import ApprovalActionModal from '../components/Modals/ApprovalActionModal';
import {
  deriveApprovalStatus,
  getActivePendingRow,
} from '../utils/approvalUtils';

import { categoryService }    from '../services/categoryService';
import { subCategoryService } from '../services/subCategoryService';
import { brandService }       from '../services/brandService';

const STATUS_COLORS = {
  draft:      'info',
  approved:   'success',
  rejected:   'error',
  correction: 'warning',
};

// ── Pending-status detection ────────────────────────────────────────────
const PENDING_PREFIXES = ['pending', 'awaiting', 'draft'];

const isPendingStatus = (status) => {
  const s = String(status || '').toLowerCase().trim();
  if (!s) return true;
  return PENDING_PREFIXES.some((p) => s.startsWith(p));
};

const hasRejection = (asset) =>
  (Array.isArray(asset?.approverList) ? asset.approverList : []).some((r) => {
    const s = String(r?.status || '').toLowerCase();
    return s.includes('rejected') || s.includes('return') || s.includes('correction');
  });

const getDisplayStatus = (asset, approvalStatuses) => {
  if (hasRejection(asset)) return 'Returned';
  const list = Array.isArray(asset?.approverList) ? asset.approverList : [];
  
  const pendingRow = list.find((r) => isPendingStatus(r.status));
  if (pendingRow) {
    return pendingRow.status || `Pending Level ${pendingRow.level}`;
  }
  
  const latest = list.reduce(
    (max, r) => (Number(r.level) > Number(max.level) ? r : max),
    list[0],
  );
  return latest?.status || 'Approved';
};

const getStatusChipColor = (label) => {
  const s = String(label || '').toLowerCase();
  if (s === 'draft')          return STATUS_COLORS.draft;
  if (s === 'created')        return STATUS_COLORS.draft;
  if (s === 'rejected')       return STATUS_COLORS.rejected;
  if (s === 'return')         return STATUS_COLORS.correction;
  if (s === 'returned')       return STATUS_COLORS.correction;
  if (s === 'correction')     return STATUS_COLORS.correction;
  if (s.includes('approved')) return STATUS_COLORS.approved;
  if (s.includes('pending'))  return 'info';
  return 'default';
};

const getMyLatestAction = (asset, userEmail) => {
  const list = Array.isArray(asset?.approvalHistory) ? asset.approvalHistory : [];
  const mine = list.filter(
    (h) =>
      String(h?.createdByEmail || '').toLowerCase().trim() ===
      String(userEmail || '').toLowerCase().trim(),
  );
  if (mine.length === 0) return null;
  return mine[mine.length - 1];
};

const formatCurrency = (value) => {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(n);
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatDateOnly = (value) => {
  if (!value) return '—';
  const d = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const MyApprovalsPage = () => {
  const { currentUser } = useAuth();
  const { showToast }   = useToast();

  const [tab, setTab]                     = useState(0);
  const [pendingAssets, setPendingAssets] = useState([]);
  const [historyAssets, setHistoryAssets] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState('');
  const [approverEnabled, setApproverEnabled] = useState(false);

  const [approvalStatuses, setApprovalStatuses] = useState([]);
  const [approverChain, setApproverChain]       = useState([]);

  const [categoryNameById,    setCategoryNameById]    = useState({});
  const [subCategoryNameById, setSubCategoryNameById] = useState({});
  const [brandNameById,       setBrandNameById]       = useState({});

  const [selectedAsset, setSelectedAsset] = useState(null);
  const [actionOpen, setActionOpen]       = useState(false);

  const userEmail = String(currentUser?.email || '').toLowerCase().trim();

  const loadAll = useCallback(async () => {
    if (!currentUser?.companyId || !currentUser?.email) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const enabled = await preferencesService.isApproverEnabled(currentUser);
      setApproverEnabled(Boolean(enabled));

      if (!enabled) {
        setPendingAssets([]);
        setHistoryAssets([]);
        setApprovalStatuses([]);
        setApproverChain([]);
        setLoading(false);
        return;
      }

      const [
        svlItems,
        allAssets,
        categoryList,
        subCategoryList,
        brandList,
      ] = await Promise.all([
        singleValueListService.getApproverItems(currentUser.companyId),
        assetService.fetchAssets({ companyId: currentUser.companyId }),
        categoryService
          .fetchCategories({ companyId: currentUser.companyId })
          .catch(() => []),
        subCategoryService
          .fetchSubCategories({ companyId: currentUser.companyId })
          .catch(() => []),
        brandService
          .fetchBrands({ companyId: currentUser.companyId })
          .catch(() => []),
      ]);

      const statuses = (svlItems || [])
        .sort((a, b) => (a.level || 0) - (b.level || 0))
        .map((r) => ({
          level: r.level,
          status: r.status,
          isFirst: r.isFirst,
          isLast: r.isLast,
        }));

      const chain = [];
      for (const row of svlItems || []) {
        for (const em of row.users || []) {
          chain.push({
            level: row.level,
            label: row.label,
            status: row.status,
            email: String(em).toLowerCase().trim(),
          });
        }
      }

      setApprovalStatuses(statuses);
      setApproverChain(chain);

      setCategoryNameById(
        Object.fromEntries(
          (categoryList || []).map((c) => [
            c.id,
            c.categoryName || c.name || '',
          ]),
        ),
      );
      setSubCategoryNameById(
        Object.fromEntries(
          (subCategoryList || []).map((s) => [
            s.id,
            s.subCategoryName || s.name || '',
          ]),
        ),
      );
      setBrandNameById(
        Object.fromEntries(
          (brandList || []).map((b) => [
            b.id,
            b.brandName || b.name || '',
          ]),
        ),
      );

      // Check if user is in the `users` array for the active pending level
          const pending = (allAssets || []).filter((a) => {
        if (hasRejection(a)) return false;
        const activeRow = getActivePendingRow(a);
        if (!activeRow) return false;
        const rowUsers = Array.isArray(activeRow.users) ? activeRow.users : [];
        const iAmApprover = rowUsers.some(
          (email) => String(email).trim().toLowerCase() === userEmail,
        );
        if (!iAmApprover) return false;

        // Don't show the row again after I've already approved this level.
        const approvedByMe = (activeRow.approvedUsers || []).some(
          (e) => String(e).trim().toLowerCase() === userEmail,
        );
        return !approvedByMe;
      });

      const history = (allAssets || []).filter((a) =>
        (a.approvalHistory || []).some(
          (h) =>
            String(h.createdByEmail || '').toLowerCase().trim() === userEmail,
        ),
      );

      setPendingAssets(pending);
      setHistoryAssets(history);
    } catch (err) {
      console.warn('[MyApprovals] load failed:', err);
      showToast(err.message || 'Failed to load approvals.', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentUser, showToast, userEmail]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const filteredList = useMemo(() => {
    const list = tab === 0 ? pendingAssets : historyAssets;
    if (!search.trim()) return list;
    const q = search.toLowerCase().trim();
    return list.filter((a) =>
      String(a.assetName || '').toLowerCase().includes(q) ||
      String(a.assetId   || '').toLowerCase().includes(q) ||
      String(a.createdByName || '').toLowerCase().includes(q) ||
      String(a.assetType || '').toLowerCase().includes(q) ||
      String(a.categoryName || categoryNameById[a.categoryId] || '')
        .toLowerCase().includes(q) ||
      String(a.subCategoryName || subCategoryNameById[a.subCategoryId] || '')
        .toLowerCase().includes(q) ||
      String(a.brandName || brandNameById[a.brandId] || '')
        .toLowerCase().includes(q) ||
      String(getDisplayStatus(a, approvalStatuses)).toLowerCase().includes(q)
    );
  }, [
    tab,
    pendingAssets,
    historyAssets,
    search,
    approvalStatuses,
    categoryNameById,
    subCategoryNameById,
    brandNameById,
  ]);

  const openAction = (asset) => {
    if (tab !== 0) return;
    setSelectedAsset(asset);
    setActionOpen(true);
  };
   const handleConfirm = useCallback(
    async ({ level, action, statusLabel, comment }) => {
      const asset = selectedAsset;
      if (!asset) return;
      
      try {
        const now = new Date().toISOString();
        const actedLevel = Number(level) || 1;

        // 1. Call the backend service
        const result = await assetService.handleApprovalAction(
          asset.id,
          actedLevel,
          userEmail,
          action,
          comment,
        );

        // 2. OVERWRITE the status that the backend just forced to "Approved Level X"
        const updatedApproverList = (result.approverList || []).map((row) => {
          if (Number(row.level) === actedLevel) {
            return {
              ...row,
              // If it's a rejection, we keep "Return". Otherwise, use the exact SVL status (e.g., "Reviewed").
              status: action === 'reject' ? 'Return' : statusLabel,
            };
          }
          return row;
        });

        // 3. Add entry to approval history
        const historyEntry = {
          level: actedLevel,
          status: action === 'reject' ? 'Return' : statusLabel,
          createdBy: currentUser?.id || currentUser?.uid || null,
          createdByEmail: userEmail,
          createdAt: now,
          comment: comment || '',
        };

        const updatedHistory = [
          ...(Array.isArray(result.approvalHistory)
            ? result.approvalHistory
            : []),
          historyEntry,
        ];

        // 4. Save the corrected approverList and history back to the database
        await assetService.upsertData(
          asset.id,
          { 
            ...result, 
            approverList: updatedApproverList, 
            approvalHistory: updatedHistory 
          },
          { isCreation: false },
        );

        // Audit log
        try {
          await logAuditEvent({
            actionType:  'approval',
            entityType:  'Asset',
            entityId:    asset.id,
            description: `Approval action "${action}" — Level ${actedLevel} → ${statusLabel}`,
            companyId:   asset.companyId,
            userId:      currentUser?.uid,
          });
        } catch (auditErr) {
          console.warn('Audit log failed for approval:', auditErr);
        }

        // Send emails based on the new state
        const updatedRow = updatedApproverList.find(
          (r) => Number(r.level) === actedLevel,
        );
        const approvedSet = new Set(
          (updatedRow?.approvedUsers || []).map((e) =>
            String(e).toLowerCase(),
          ),
        );
        const allUsersAtLevel = (updatedRow?.users || []).map((e) =>
          String(e).toLowerCase(),
        );
        const allApproved =
          allUsersAtLevel.length > 0 &&
          allUsersAtLevel.every((e) => approvedSet.has(e));

        // If it's the final level and approved, it's fully complete
        const isFinalLevel = !updatedApproverList.some(
          (r) => Number(r.level) > actedLevel
        );

        const isLevelComplete =
          action === 'reject' ||
          allApproved ||
          (isFinalLevel && action !== 'reject');

        if (action === 'reject') {
          await sendApprovalStatusEmail({
            creatorEmail: asset.createdByEmail,
            creatorName: asset.createdByName || '',
            assetId: asset.assetId,
            assetName: asset.assetName,
            status: 'returned',
            approverName: currentUser?.displayName || currentUser?.name || '',
            approverEmail: userEmail,
            levelLabel: `Level ${actedLevel}`,
            comments: comment,
            companyName: asset.companyName || currentUser?.company || '',
          });
        } else if (isLevelComplete) {
          const sortedLevels = [...updatedApproverList]
            .map((r) => Number(r.level))
            .filter((n) => Number.isFinite(n))
            .sort((a, b) => a - b);
          const nextLevel = sortedLevels.find((lvl) => lvl > actedLevel);
          const nextRow = updatedApproverList.find(
            (r) => Number(r.level) === nextLevel,
          );
          
          if (nextRow && Array.isArray(nextRow.users) && nextRow.users.length > 0) {
            for (const email of nextRow.users) {
              await sendNextLevelApprovalEmail({
                nextApproverEmail: email,
                nextApproverName: '',
                assetId: asset.assetId,
                assetName: asset.assetName,
                levelLabel: nextRow.label || `Level ${nextLevel}`,
                level: nextLevel,
                previousApproverName:
                  currentUser?.displayName || currentUser?.name || '',
                previousApproverEmail: userEmail,
                previousLevelLabel: `Level ${actedLevel}`,
                creatorEmail: asset.createdByEmail,
                creatorName: asset.createdByName || '',
                companyName: asset.companyName || currentUser?.company || '',
              });
            }
          } else {
            await sendApprovalStatusEmail({
              creatorEmail: asset.createdByEmail,
              creatorName: asset.createdByName || '',
              assetId: asset.assetId,
              assetName: asset.assetName,
              status: 'approved',
              approverName: currentUser?.displayName || currentUser?.name || '',
              approverEmail: userEmail,
              levelLabel: `Level ${actedLevel}`,
              comments: comment,
              companyName: asset.companyName || currentUser?.company || '',
            });
          }
        }

        showToast(
          `Approval recorded: ${asset.assetName} → ${statusLabel}`,
          'success',
        );

        window.dispatchEvent(new CustomEvent('approvals-updated'));

        setActionOpen(false);
        setSelectedAsset(null);
        await loadAll();
      } catch (err) {
        showToast(err.message || 'Failed to record approval.', 'error');
      }
    },
    [selectedAsset, currentUser, showToast, loadAll, userEmail],
  );

  if (!currentUser) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="warning">Please sign in.</Alert>
      </Container>
    );
  }

  if (!approverEnabled && !loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          The approval workflow is disabled for your company. Ask an admin to
          enable it under <strong>Settings → Preferences → Approval Workflow</strong>.
        </Alert>
      </Container>
    );
  }

  const pendingCount = pendingAssets.length;
  const isHistoryTab = tab === 1;
  const columnCount = isHistoryTab ? 5 : 17;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>

      <Box>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, letterSpacing: '-0.5px', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <HowToRegIcon sx={{ fontSize: '2rem', color: 'primary.main' }} />
          My Approvals
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Assets waiting for your approval at the current level
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab
            icon={<HourglassEmptyIcon fontSize="small" />}
            iconPosition="start"
            label={`Pending (${pendingCount})`}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
          <Tab
            icon={<HistoryIcon fontSize="small" />}
            iconPosition="start"
            label={`My Actions (${historyAssets.length})`}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
        </Tabs>

        <Box sx={{ p: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by asset ID, name, type, category, brand, or creator..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'action.active' }} />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: { borderRadius: 2 },
            }}
          />
        </Box>
      </Paper>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 3, maxWidth: '100%', overflowX: 'auto' }}
      >
        <Table sx={{ minWidth: isHistoryTab ? undefined : 1700 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Asset ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Asset Name</TableCell>

              {isHistoryTab ? (
                <>
                  <TableCell sx={{ fontWeight: 700 }}>My Action</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Comment</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Action At</TableCell>
                </>
              ) : (
                <>
                  <TableCell sx={{ fontWeight: 700 }}>Asset Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Sub-Category</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Brand</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Qty</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Purchase Value</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Purchase Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Warranty Expiry</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Depreciation</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Created By</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Submitted</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Current Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Level</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: columnCount }).map((__, j) => (
                    <TableCell key={j}><Skeleton variant="text" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : filteredList.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount} sx={{ py: 6, textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: '3rem', color: 'success.light' }} />
                    <Typography variant="body1" fontWeight={600}>
                      {tab === 0 ? 'Nothing pending — you are all caught up!' : 'No history yet.'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tab === 0
                        ? 'Assets awaiting your approval will appear here.'
                        : 'Assets you have acted on will appear here.'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredList.map((row) => {
                const myAction = isHistoryTab
                  ? getMyLatestAction(row, userEmail)
                  : null;

                const displayStatus = isHistoryTab
                  ? (myAction?.status || '—')
                  : getDisplayStatus(row, approvalStatuses);

                const chipColor = getStatusChipColor(displayStatus);
                const isPending = tab === 0;
                const isRejected =
                  String(displayStatus).toLowerCase() === 'rejected' ||
                  String(displayStatus).toLowerCase() === 'return' ||
                  String(displayStatus).toLowerCase() === 'returned';

                const categoryLabel =
                  row.categoryName || categoryNameById[row.categoryId] || '—';
                const subCategoryLabel =
                  row.subCategoryName || subCategoryNameById[row.subCategoryId] || '—';
                const brandLabel =
                  row.brandName || brandNameById[row.brandId] || '—';

                const creatorLabel =
                  row.createdByName ||
                  row.createdByEmail ||
                  row.approvalHistory?.find((h) => Number(h?.level) === 0)
                    ?.createdByEmail ||
                  '—';

                // Get current pending level info
                const activeRow = getActivePendingRow(row);
                const pendingLevel = activeRow?.level || null;

                return (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{
                      fontFamily: 'monospace',
                      fontWeight: 600,
                      color: isRejected ? 'error.main' : 'primary.main',
                      whiteSpace: 'nowrap',
                    }}>
                      {row.assetId || '—'}
                    </TableCell>
                    <TableCell sx={{
                      fontWeight: 600,
                      color: isRejected ? 'error.main' : 'inherit',
                    }}>
                      {row.assetName || '—'}
                    </TableCell>

                    {isHistoryTab ? (
                      <>
                        <TableCell>
                          <Chip
                            label={displayStatus}
                            size="small"
                            color={chipColor}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>

                        <TableCell
                          sx={{
                            color: 'text.secondary',
                            maxWidth: 320,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {myAction?.comment || '—'}
                        </TableCell>

                        <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {formatDate(myAction?.createdAt)}
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {row.assetType || '—'}
                        </TableCell>

                        <TableCell sx={{ color: 'text.secondary' }}>
                          {categoryLabel}
                        </TableCell>

                        <TableCell sx={{ color: 'text.secondary' }}>
                          {subCategoryLabel}
                        </TableCell>

                        <TableCell sx={{ color: 'text.secondary' }}>
                          {brandLabel}
                        </TableCell>

                        <TableCell align="right" sx={{ color: 'text.secondary' }}>
                          {row.quantity != null && row.quantity !== ''
                            ? row.quantity
                            : '—'}
                        </TableCell>

                        <TableCell align="right" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {formatCurrency(row.purchaseValue)}
                        </TableCell>

                        <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {row.purchaseDate ? formatDateOnly(row.purchaseDate) : '—'}
                        </TableCell>

                        <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {row.warrantyExpiry ? formatDateOnly(row.warrantyExpiry) : '—'}
                        </TableCell>

                        <TableCell sx={{ color: 'text.secondary' }}>
                          {row.depreciationMethod || '—'}
                        </TableCell>

                        <TableCell sx={{ color: 'text.secondary' }}>
                          {creatorLabel}
                        </TableCell>

                        <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {formatDate(row.createdAt || row.metadata?.createdAt)}
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={displayStatus}
                            size="small"
                            color={chipColor}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={pendingLevel ? `Awaiting Level ${pendingLevel}` : '—'}
                            size="small"
                            variant="outlined"
                            sx={{ whiteSpace: 'nowrap' }}
                          />
                        </TableCell>

                        <TableCell align="right">
                          {isPending && (
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={<HowToRegIcon />}
                              onClick={() => openAction(row)}
                              disableElevation
                              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, whiteSpace: 'nowrap' }}
                            >
                              Take Action
                            </Button>
                          )}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {actionOpen && selectedAsset && (
        <ApprovalActionModal
          open={actionOpen}
          asset={selectedAsset}
          currentUser={currentUser}
          approvalStatuses={approvalStatuses}
          approverChain={approverChain}
          onClose={() => { setActionOpen(false); setSelectedAsset(null); }}
          onConfirm={handleConfirm}
        />
      )}
    </Container>
  );
};

export default MyApprovalsPage;